import { TabsComponent } from "@/components/core/tabs-component";
import { Link } from "@/components/core/tanstack-link";
import { ActivityReport } from "@/components/reports/activity-report";
import { CustomersReport } from "@/components/reports/customers-report";
import { InvoicesReport } from "@/components/reports/invoices-report";
import { ReportSchedules } from "@/components/reports/schedule";
import { queryClient } from "@/helpers/tanstack-client";
import { ReportableEntity, getGetReportQueryKey, getReport, useGetReport } from "@/models/backend";
import { Box, Breadcrumbs, Stack, Typography } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { ComponentProps } from "react";
import { z } from "zod";

const tabs = ["explore", "schedule"] as const;

const schema = z.object({
  tab: z.enum(tabs).default("explore").catch("explore"),
});

export const Route = createFileRoute("/_authenticated/_contained/reports/$reportId")({
  component: RouteComponent,
  validateSearch: schema,
  loader: async ({ params: { reportId } }) => {
    if (reportId in ReportableEntity) return null;
    await queryClient.ensureQueryData({
      queryFn: () => getReport(reportId),
      queryKey: getGetReportQueryKey(reportId),
    });
  },
});

function RouteComponent() {
  const { reportId } = Route.useParams();
  const { data: report } = useGetReport(reportId, {
    query: { enabled: !(reportId in ReportableEntity) },
  });
  const entity =
    report?.entity ?? (reportId in ReportableEntity ? (reportId as ReportableEntity) : null);

  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const tabObjs: (ComponentProps<typeof TabsComponent>["tabs"][number] & {
    id: (typeof tabs)[number];
  })[] = [
    {
      id: "explore",
      label: "Data",
      component:
        (entity == ReportableEntity.invoice && <InvoicesReport report={report} />) ||
        (entity == ReportableEntity.customer && <CustomersReport report={report} />) ||
        (entity == ReportableEntity.event && <ActivityReport report={report} />),
    },
    {
      id: "schedule",
      label: "Schedule",
      component: report != null && (
        <ReportSchedules
          reportId={report.id}
          notifications={report?.notifications ?? []}
          entity={entity}
        />
      ),
      extraProps: report?.id ? {} : { disabled: true },
    },
  ];

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator=">">
        <Link to="/reports">
          <Typography>Reports</Typography>
        </Link>
        {entity ? (
          <Link to="/reports" search={{ tab: entity }}>
            <Typography>
              {entity == ReportableEntity.customer
                ? "Customers"
                : entity == ReportableEntity.invoice
                  ? "Invoices"
                  : "Activity"}
            </Typography>
          </Link>
        ) : null}
        <Typography>{report?.name ?? "New report"}</Typography>
      </Breadcrumbs>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
        <Stack spacing={2}>
          <Typography variant="h4">{report?.name ?? "New report"}</Typography>
          <Typography>{report?.description}</Typography>
        </Stack>
        <Box sx={{ marginLeft: "auto" }} />
      </Stack>
      <Stack sx={{ width: "100%" }}>
        <TabsComponent
          tabs={tabObjs}
          tabsProps={!report ? { title: "Save the report before scheduling" } : {}}
          selectedTab={tabObjs.findIndex((tab) => tab.id === search.tab)}
          onSelectedTab={(idx) => {
            navigate({ search: { tab: tabObjs[idx].id } });
          }}
        />
      </Stack>{" "}
    </Stack>
  );
}
